import { useEffect, useMemo, useState } from "react";
import { ArrowsLeftRight, Check, IdentificationCard, Key, MagnifyingGlass, PencilSimple, Trash, UploadSimple, UserCheck, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { notifyContentUpdated, subscribeContentUpdates } from "../../lib/contentSync";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import TimeProgressChart from "../TimeProgressChart";
import { waLink } from "./DuesMatrix";

const CLASS_OPTIONS = [
    "Tim Hijau (Pemula)",
    "Tim Kuning",
    "Tim Jingga",
    "Tim Biru",
    "Tim Pink",
    "Tim Merah",
    "Speed",
    "Slalom",
];

function SectionTitle({ children }) {
    return <h4 className="text-[10px] uppercase tracking-[0.25em] text-racing-golddeep border-b border-racing-line pb-2 mb-3 mt-7 first:mt-0">{children}</h4>;
}

function toInputDate(value) {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return "";
    const [, day, month, year] = match;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

export default function AdminAthletesTab() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const isFinance = user?.role === "finance";
    const [athletes, setAthletes] = useState([]);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [dossier, setDossier] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [editDialog, setEditDialog] = useState("");
    const [studentForm, setStudentForm] = useState(null);
    const [publicForm, setPublicForm] = useState(null);
    const [accountForm, setAccountForm] = useState(null);
    const [passwordForm, setPasswordForm] = useState({ new_password: "" });
    const [passwordResult, setPasswordResult] = useState(null);
    const [saving, setSaving] = useState(false);
    const [transferMode, setTransferMode] = useState(false);
    const [transferIds, setTransferIds] = useState([]);
    const [targetClass, setTargetClass] = useState("");
    const [transferSaving, setTransferSaving] = useState(false);
    const [duplicateSuggestions, setDuplicateSuggestions] = useState([]);
    const [duplicateMerging, setDuplicateMerging] = useState("");
    const [activateForm, setActivateForm] = useState(null);
    const [activateResult, setActivateResult] = useState(null);
    const [activating, setActivating] = useState(false);
    const [activatePhotoUploading, setActivatePhotoUploading] = useState(false);
    const [slideshowStates, setSlideshowStates] = useState([]);
    const [slideshowClass, setSlideshowClass] = useState("");
    const [slideshowSort, setSlideshowSort] = useState("nia");
    const [slideshowBusy, setSlideshowBusy] = useState(false);
    const [supportData, setSupportData] = useState(null);

    const loadAthletes = async () => {
        try {
            const res = await api.get("/admin/active-athletes");
            setAthletes(res.data);
            if (isAdmin) {
                const duplicateRes = await api.get("/admin/active-athlete-duplicate-suggestions");
                setDuplicateSuggestions(duplicateRes.data);
            } else {
                setDuplicateSuggestions([]);
            }
        } catch {
            toast.error("Gagal memuat data atlet");
        }
    };

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await api.get("/admin/active-athletes");
                if (!active) return;
                setAthletes(res.data);
                if (isAdmin) {
                    const duplicateRes = await api.get("/admin/active-athlete-duplicate-suggestions");
                    if (active) setDuplicateSuggestions(duplicateRes.data);
                }
            } catch {
                if (active) toast.error("Gagal memuat data atlet");
            }
        })();
        return () => { active = false; };
    }, [isAdmin]);

    useEffect(() => {
        if (!isAdmin && !isFinance) setTransferMode(true);
    }, [isAdmin]);

    const [kuFilter, setKuFilter] = useState("");
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return athletes.filter((a) =>
            (!q || a.name.toLowerCase().includes(q)) &&
            (!kuFilter || a.ku === kuFilter)
        );
    }, [athletes, search, kuFilter]);

    const groups = useMemo(() => {
        const map = {};
        filtered.forEach((a) => {
            const key = a.class_name || "Tanpa Kelas";
            (map[key] = map[key] || []).push(a);
        });
        return Object.keys(map).sort().map((name) => ({ name, items: map[name] }));
    }, [filtered]);

    const selectedTransferAthletes = useMemo(
        () => athletes.filter((athlete) => transferIds.includes(athlete.id)),
        [athletes, transferIds]
    );

    const toggleTransferMode = () => {
        setTransferMode((current) => !current);
        setTransferIds([]);
        setTargetClass("");
    };

    const toggleTransferAthlete = (athlete) => {
        if (athlete.kind !== "student") {
            toast.error("Atlet publik tanpa akun siswa tidak bisa dipindah lewat menu ini");
            return;
        }
        setTransferIds((current) => current.includes(athlete.id) ? current.filter((id) => id !== athlete.id) : [...current, athlete.id]);
    };

    const handleAthleteClick = (athlete) => {
        if (transferMode || (!isAdmin && !isFinance)) {
            toggleTransferAthlete(athlete);
            return;
        }
        openDetail(athlete);
    };

    const submitClassTransfer = async () => {
        if (!transferIds.length) {
            toast.error("Pilih minimal satu siswa");
            return;
        }
        if (!targetClass) {
            toast.error("Pilih kelas tujuan");
            return;
        }
        const names = selectedTransferAthletes.map((athlete) => athlete.name).join(", ");
        if (!window.confirm(`Pindahkan ${transferIds.length} siswa ke ${targetClass}?\n\n${names}\n\nRiwayat absensi, iuran, rapot, dan catatan waktu akan ikut memakai kelas baru.`)) return;
        setTransferSaving(true);
        try {
            const { data } = await api.post("/coach/class-transfer", {
                athlete_ids: transferIds,
                target_class: targetClass,
            });
            toast.success(data.message || "Siswa berhasil dipindah kelas");
            notifyContentUpdated();
            setTransferIds([]);
            setTargetClass("");
            await loadAthletes();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setTransferSaving(false);
        }
    };

    const mergeDuplicate = async (suggestion) => {
        if (!window.confirm(`Gabungkan data "${suggestion.source.name}" ke "${suggestion.target.name}"? Riwayat prestasi dan operasional akan mengikuti data tujuan, lalu data asal dihapus.`)) return;
        setDuplicateMerging(suggestion.id);
        try {
            const { data } = await api.post("/admin/active-athletes/merge", {
                source_id: suggestion.source.id,
                target_id: suggestion.target.id,
            });
            toast.success(data.message || "Data ganda digabung");
            notifyContentUpdated();
            await loadAthletes();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setDuplicateMerging("");
        }
    };

    const openDetail = async (athlete) => {
        setSelected(athlete);
        setDossier(null);
        setLoadingDetail(true);
        try {
            const endpoint = athlete.kind === "public" ? `/admin/athlete-dossier/${athlete.id}` : `/admin/student-dossier/${athlete.id}`;
            const { data } = await api.get(endpoint);
            setDossier(data);
        } catch {
            toast.error("Gagal memuat detail atlet");
        } finally {
            setLoadingDetail(false);
        }
    };

    const refreshCurrentDossier = async () => {
        if (!selected) return;
        const endpoint = selected.kind === "public" ? `/admin/athlete-dossier/${selected.id}` : `/admin/student-dossier/${selected.id}`;
        const { data } = await api.get(endpoint);
        setDossier(data);
    };

    const openActivate = () => {
        if (!dossier) return;
        setActivateResult(null);
        setActivateForm({
            photo_url: dossier.student?.photo_url || "",
            username: dossier.student?.parent_username || dossier.student?.email || dossier.parent?.original_username || dossier.parent?.username || "",
            password: "",
        });
    };

    const loadSlideshow = async () => {
        try {
            const { data } = await api.get("/admin/athlete-slideshow");
            setSlideshowStates(data.states || []);
        } catch (err) {
            // abaikan — panel video bersifat opsional
        }
    };

    const allClasses = useMemo(
        () => [...new Set(athletes.map((a) => a.class_name).filter(Boolean))].sort(),
        [athletes]
    );

    const slideshow = useMemo(
        () => slideshowStates.find((s) => (s.class_name || "") === slideshowClass) || null,
        [slideshowStates, slideshowClass]
    );

    useEffect(() => {
        if (isAdmin) loadSlideshow();
    }, [isAdmin]);

    useEffect(() => {
        if (!slideshowStates.some((s) => s.status === "generating")) return undefined;
        const timer = setInterval(loadSlideshow, 4000);
        return () => clearInterval(timer);
    }, [slideshowStates]);

    const regenerateSlideshow = async () => {
        setSlideshowBusy(true);
        try {
            const { data } = await api.post("/admin/athlete-slideshow/generate", { class_name: slideshowClass, sort_by: slideshowSort });
            toast.success(data.message || "Pembuatan video dimulai");
            setSlideshowStates((prev) => {
                const others = prev.filter((s) => (s.class_name || "") !== slideshowClass);
                return [...others, { class_name: slideshowClass, status: "generating" }];
            });
            loadSlideshow();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSlideshowBusy(false);
        }
    };

    useEffect(() => {
        if (!isAdmin || !dossier) {
            setSupportData(null);
            return;
        }
        const publicId = dossier.athlete?.id || (selected?.kind === "public" ? selected.id : null);
        if (!publicId) {
            setSupportData(null);
            return;
        }
        api.get(`/admin/athlete-support?athlete_id=${publicId}`)
            .then((r) => setSupportData(r.data))
            .catch(() => setSupportData(null));
    }, [dossier, isAdmin]);

    const deleteSupport = async (supportId) => {
        if (!window.confirm("Hapus dukungan ini secara permanen?")) return;
        try {
            await api.delete(`/admin/athlete-support/${supportId}`);
            setSupportData((prev) => prev ? { ...prev, comments: prev.comments.filter((c) => c.id !== supportId) } : prev);
            toast.success("Dukungan dihapus");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const uploadActivatePhoto = async (photoFile) => {
        if (!photoFile) return;
        setActivatePhotoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", photoFile);
            const { data } = await api.post("/admin/upload", fd);
            setActivateForm((prev) => ({ ...prev, photo_url: data.url }));
            toast.success("Foto terunggah");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setActivatePhotoUploading(false);
        }
    };

    const submitActivate = async () => {
        if (!activateForm?.username?.trim()) {
            toast.error("Username wajib diisi");
            return;
        }
        setActivating(true);
        try {
            const { data } = await api.post(`/admin/imported-records/${selected.id}/activate`, activateForm);
            setActivateResult(data);
            toast.success(data.message);
            notifyContentUpdated();
            loadAthletes();
            refreshCurrentDossier();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setActivating(false);
        }
    };

    useEffect(() => {
        return subscribeContentUpdates(async () => {
            try {
                const listResponse = await api.get("/admin/active-athletes");
                setAthletes(listResponse.data);
                if (selected?.id) {
                    const endpoint = selected.kind === "public" ? `/admin/athlete-dossier/${selected.id}` : `/admin/student-dossier/${selected.id}`;
                    const detailResponse = await api.get(endpoint);
                    setDossier(detailResponse.data);
                }
            } catch {
                // Pembaruan berikutnya akan mencoba memuat ulang kembali.
            }
        });
    }, [selected?.id, selected?.kind]);

    const deleteSelectedAthlete = async () => {
        if (!selected) return;
        const message = selected.kind === "public"
            ? `Hapus atlet publik ${selected.name}? Data atlet akan hilang dari daftar aktif dan profil publik.`
            : `Hapus data atlet ${selected.name}? Data siswa dan profil publiknya akan dihapus. Riwayat prestasi/operasional yang sudah tercatat tetap tersimpan.`;
        if (!window.confirm(message)) return;
        setSaving(true);
        try {
            const endpoint = selected.kind === "public" ? `/admin/athletes/${selected.id}` : `/admin/student-records/${selected.id}`;
            await api.delete(endpoint);
            toast.success(`Data atlet ${selected.name} dihapus`);
            setSelected(null);
            setDossier(null);
            notifyContentUpdated();
            loadAthletes();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const openStudentEdit = () => {
        const student = dossier.student;
        setStudentForm({
            name: student.name || "",
            photo_url: student.photo_url || "",
            nickname: student.nickname || "",
            nia: dossier.athlete?.nia || "",
            class_name: student.class_name || "",
            status: student.status || "Aktif",
            gender: student.gender || "",
            birth_place: student.birth_place || "",
            birth_date: toInputDate(student.birth_date),
            joined_year: Number(student.joined_date) || new Date().getFullYear(),
            joined_day: student.joined_day || "",
            joined_month: student.joined_month || "",
            address: student.address || "",
            address_kk: student.address_kk || "",
            religion: student.religion || "",
            referral_source: student.referral_source || "",
            promo_code: student.promo_code || "",
        });
        setEditDialog("student");
    };

    const openPublicEdit = () => {
        const athlete = dossier.athlete;
        setPublicForm({
            nia: athlete.nia || "",
            name: athlete.name || "",
            class_name: athlete.class_name || "",
            birth_year: athlete.birth_year || new Date().getFullYear(),
            joined_year: athlete.joined_year || new Date().getFullYear(),
            photo_url: athlete.photo_url || "",
            status: athlete.status || "active",
            order: athlete.order || 0,
        });
        setEditDialog("athlete");
    };

    const openAccountEdit = () => {
        const parent = dossier.parent;
        setAccountForm({
            username: parent.username || "",
            email: parent.email || "",
            whatsapp: parent.contact_phone || "",
            father_name: parent.father_name || "",
            mother_name: parent.mother_name || "",
            status: parent.status || "active",
        });
        setEditDialog("account");
    };

    const openPasswordEdit = () => {
        setPasswordForm({ new_password: "" });
        setPasswordResult(null);
        setEditDialog("password");
    };

    const saveStudent = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/student-records/${selected.id}`, {
                ...studentForm,
                joined_year: Number(studentForm.joined_year),
                joined_day: Number(studentForm.joined_day || 0),
            });
            setDossier((prev) => ({
                ...prev,
                student: data,
                athlete: prev.athlete ? { ...prev.athlete, name: data.name, class_name: data.class_name, photo_url: data.photo_url, nia: studentForm.nia } : prev.athlete,
            }));
            setSelected((prev) => ({ ...prev, name: data.name, class_name: data.class_name, photo_url: data.photo_url, nia: studentForm.nia }));
            setEditDialog("");
            notifyContentUpdated();
            loadAthletes();
            toast.success("Data atlet diperbarui");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const savePublicAthlete = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/athletes/${selected.id}`, {
                ...publicForm,
                birth_year: Number(publicForm.birth_year),
                joined_year: Number(publicForm.joined_year),
                order: Number(publicForm.order || 0),
            });
            setDossier((prev) => ({ ...prev, athlete: data, student: { ...prev.student, name: data.name, class_name: data.class_name, photo_url: data.photo_url } }));
            setSelected((prev) => ({ ...prev, name: data.name, class_name: data.class_name, photo_url: data.photo_url, nia: data.nia }));
            setEditDialog("");
            notifyContentUpdated();
            loadAthletes();
            toast.success("Data atlet diperbarui");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const saveAccount = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/parents/${dossier.parent.id}`, accountForm);
            setDossier((prev) => ({ ...prev, parent: data }));
            setEditDialog("");
            await refreshCurrentDossier();
            toast.success("Akun orang tua diperbarui");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const reactivateParent = async () => {
        if (!dossier?.parent) return;
        if (!window.confirm(`Aktifkan kembali akun orang tua ini? Username/email asli (${dossier.parent.original_username || dossier.parent.original_email || "-"}) akan dipulihkan dan orang tua bisa login lagi dengan password terakhirnya.`)) return;
        setSaving(true);
        try {
            const { data } = await api.post(`/admin/parents/${dossier.parent.id}/reactivate`);
            toast.success(data.message);
            await refreshCurrentDossier();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.post(`/admin/parents/${dossier.parent.id}/password`, passwordForm);
            setPasswordResult(data);
            toast.success("Password orang tua diperbarui");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const attendanceStats = dossier
        ? dossier.attendance.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {})
        : {};

    const timeByRace = useMemo(() => {
        if (!dossier) return [];
        const map = {};
        dossier.time_records.forEach((r) => {
            const key = (r.race_number || "").trim() || "Tanpa Nomor Lomba";
            (map[key] = map[key] || []).push(r);
        });
        return Object.keys(map).sort().map((name) => {
            const items = map[name].slice().sort((a, b) => (b.record_date || "").localeCompare(a.record_date || ""));
            const best = items.reduce((current, r) => (!current || (r.time_ms || Infinity) < (current.time_ms || Infinity) ? r : current), null);
            return { name, items, best };
        });
    }, [dossier]);

    return (
        <div data-testid="admin-athletes-tab">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div>
                    <h2 className="font-display uppercase text-2xl text-neutral-900">Siswa Aktif</h2>
                    <p className="text-sm text-racing-muted mt-2">Pilih beberapa siswa untuk dipindahkan ke kelas baru. Semua riwayat operasional ikut berpindah otomatis.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    {!isFinance && (
                    <button
                        type="button"
                        onClick={toggleTransferMode}
                        data-testid="class-transfer-toggle"
                        className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.18em] border transition-colors ${transferMode ? "bg-racing-red border-racing-red text-white" : "bg-white border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red"}`}
                    >
                        <ArrowsLeftRight size={16} weight="bold" /> {transferMode ? "Selesai Pilih" : "Pindah Kelas"}
                    </button>
                    )}
                    <div className="relative">
                        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-racing-muted" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Ketik sebagian nama atlet..."
                            data-testid="athlete-search-input"
                            className="bg-white border border-racing-line pl-10 pr-4 py-3 text-sm w-full md:w-64 focus:outline-none focus:border-racing-red transition-colors"
                        />
                    </div>
                    <select
                        value={kuFilter}
                        onChange={(e) => setKuFilter(e.target.value)}
                        data-testid="athlete-ku-filter"
                        className="bg-white border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red transition-colors"
                    >
                        <option value="">Semua KU</option>
                        {["KU A", "KU B", "KU C", "KU D", "Junior", "Senior"].map((ku) => <option key={ku} value={ku}>{ku}</option>)}
                    </select>
                </div>
            </div>

            {transferMode && (
                <section className="mb-8 border border-racing-gold bg-white" data-testid="class-transfer-panel">
                    <div className="px-5 py-4 border-b border-racing-line bg-racing-gold/10">
                        <h3 className="font-display uppercase text-xl text-racing-golddeep">Pindah Kelas Massal</h3>
                        <p className="text-sm text-racing-muted mt-1">Klik nama siswa untuk memilih. Setelah itu pilih kelas tujuan lalu klik OK.</p>
                    </div>
                    <div className="p-5">
                        <p className="text-sm" data-testid="class-transfer-selected-count"><span className="font-medium">{selectedTransferAthletes.length}</span> siswa dipilih</p>
                        {selectedTransferAthletes.length > 0 && (
                            <div className="mt-4 grid lg:grid-cols-[1fr_280px_auto] gap-4 items-end">
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama yang Dipilih</p>
                                    <div className="flex flex-wrap gap-2" data-testid="class-transfer-selected-names">
                                        {selectedTransferAthletes.map((athlete, index) => (
                                            <button
                                                key={athlete.id}
                                                type="button"
                                                onClick={() => toggleTransferAthlete(athlete)}
                                                data-testid={`class-transfer-selected-${index}`}
                                                className="border border-racing-red bg-racing-red/5 text-racing-red px-3 py-1.5 text-xs"
                                                title="Klik untuk batal memilih"
                                            >
                                                {athlete.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas Tujuan</label>
                                    <select
                                        value={targetClass}
                                        onChange={(e) => setTargetClass(e.target.value)}
                                        data-testid="class-transfer-target"
                                        className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red"
                                    >
                                        <option value="" disabled>Pilih kelas tujuan</option>
                                        {CLASS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                                    </select>
                                </div>
                                <button
                                    type="button"
                                    onClick={submitClassTransfer}
                                    disabled={transferSaving || !targetClass}
                                    data-testid="class-transfer-submit"
                                    className="bg-racing-red text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-racing-redhover disabled:opacity-50 transition-colors"
                                >
                                    {transferSaving ? "Memindahkan..." : "OK"}
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {isAdmin && duplicateSuggestions.length > 0 && (
                <section className="mb-8 border border-racing-red bg-white" data-testid="active-duplicate-suggestions">
                    <div className="px-5 py-4 border-b border-racing-line bg-racing-red text-white">
                        <h3 className="font-display uppercase text-xl">Saran Gabung Data Atlet</h3>
                        <p className="text-xs text-white/75 mt-1">Nama-nama ini tampak merujuk pada atlet yang sama. Periksa kelas/NIA sebelum menggabungkan.</p>
                    </div>
                    <div className="divide-y divide-racing-line">
                        {duplicateSuggestions.map((suggestion, index) => (
                            <div key={suggestion.id} className="p-5 grid lg:grid-cols-[1fr_auto_1fr_auto] gap-4 items-center" data-testid={`active-duplicate-${index}`}>
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-1">Data Asal</p>
                                    <p className="font-medium text-neutral-900">{suggestion.source.name}</p>
                                    <p className="text-xs text-racing-muted">{suggestion.source.kind === "student" ? "Siswa" : "Atlet Publik"} • {suggestion.source.class_name || "Tanpa kelas"}{suggestion.source.nia ? ` • NIA ${suggestion.source.nia}` : ""}</p>
                                </div>
                                <span className="hidden lg:inline-flex px-3 py-1.5 border border-racing-gold text-racing-golddeep bg-racing-gold/10 text-[10px] uppercase tracking-[0.15em]">Mirip {Math.round(suggestion.similarity * 100)}%</span>
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-1">Data Tujuan</p>
                                    <p className="font-medium text-neutral-900">{suggestion.target.name}</p>
                                    <p className="text-xs text-racing-muted">{suggestion.target.kind === "student" ? "Siswa" : "Atlet Publik"} • {suggestion.target.class_name || "Tanpa kelas"}{suggestion.target.nia ? ` • NIA ${suggestion.target.nia}` : ""}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => mergeDuplicate(suggestion)}
                                    disabled={duplicateMerging === suggestion.id}
                                    data-testid={`active-duplicate-merge-${index}`}
                                    className="bg-racing-red text-white px-4 py-3 text-xs uppercase tracking-[0.2em] hover:bg-racing-redhover disabled:opacity-50 transition-colors"
                                >
                                    {duplicateMerging === suggestion.id ? "Menggabung..." : "Gabungkan"}
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {(search || kuFilter) && (
                <p className="text-xs text-racing-muted mb-4" data-testid="athlete-search-count">{filtered.length} atlet cocok{search ? ` dengan "${search}"` : ""}{kuFilter ? ` pada ${kuFilter}` : ""}</p>
            )}

            {isAdmin && (
                <section className="border border-racing-line bg-white p-6 mb-8" data-testid="athlete-slideshow-panel">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <h3 className="font-display uppercase text-xl">Video Slide Atlet</h3>
                            <p className="text-xs text-racing-muted mt-1">
                                Video 3:5 berisi foto atlet (0,4 detik per atlet) dengan kelas & nama. Pilih satu kelas atau semua kelas — perbarui kapan saja.
                            </p>
                            <p className="text-xs text-racing-muted mt-2" data-testid="slideshow-status">
                                {!slideshow && `Video untuk ${slideshowClass || "semua kelas"} belum pernah dibuat.`}
                                {slideshow?.status === "ready" && `Terakhir dibuat: ${new Date(slideshow.updated_at).toLocaleString("id-ID")} — ${slideshow.athlete_count} atlet — urutan ${slideshow.sort_by === "ku" ? "Kelompok Umur" : "NIA"}`}
                                {slideshow?.status === "generating" && "Sedang membuat video... (bisa memakan waktu beberapa menit)"}
                                {slideshow?.status === "error" && `Gagal: ${slideshow.error}`}
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <select
                                value={slideshowClass}
                                onChange={(e) => setSlideshowClass(e.target.value)}
                                data-testid="slideshow-class-select"
                                className="bg-racing-surface border border-racing-line px-4 py-3 text-xs"
                            >
                                <option value="">Semua Kelas</option>
                                {allClasses.map((cls) => <option key={cls} value={cls}>{cls}</option>)}
                            </select>
                            <select
                                value={slideshowSort}
                                onChange={(e) => setSlideshowSort(e.target.value)}
                                data-testid="slideshow-sort-select"
                                className="bg-racing-surface border border-racing-line px-4 py-3 text-xs"
                            >
                                <option value="nia">Urutan NIA</option>
                                <option value="ku">Urutan Kelompok Umur</option>
                            </select>
                            <button
                                onClick={regenerateSlideshow}
                                disabled={slideshowBusy || slideshow?.status === "generating"}
                                data-testid="slideshow-regenerate-button"
                                className="bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors"
                            >
                                {slideshow?.status === "generating" ? "Membuat..." : slideshow?.status === "ready" ? "Perbarui Video" : "Buat Video"}
                            </button>
                            {slideshow?.status === "ready" && slideshow.url && (
                                <a
                                    href={`${fileUrl(slideshow.url)}?v=${encodeURIComponent(slideshow.updated_at)}`}
                                    download
                                    data-testid="slideshow-download-button"
                                    className="border border-racing-line px-5 py-3 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors"
                                >
                                    Unduh MP4
                                </a>
                            )}
                        </div>
                    </div>
                    {slideshow?.status === "ready" && slideshow.url && (
                        <video
                            key={`${slideshowClass}-${slideshow.updated_at}`}
                            src={`${fileUrl(slideshow.url)}?v=${encodeURIComponent(slideshow.updated_at)}`}
                            controls
                            data-testid="slideshow-preview"
                            className="mt-4 w-40 border border-racing-line bg-neutral-100"
                        />
                    )}
                </section>
            )}

            {groups.length === 0 && <p className="border border-racing-line bg-white p-6 text-sm text-racing-muted" data-testid="athlete-search-empty">Tidak ada atlet yang cocok.</p>}

            <div className="space-y-8">
                {groups.map((group, gi) => (
                    <div key={group.name} data-testid={`athlete-active-group-${gi}`}>
                        <p className="text-xs uppercase tracking-[0.25em] text-racing-golddeep mb-3 border-b border-racing-line pb-2">{group.name} — {group.items.length} atlet</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-racing-line border border-racing-line">
                            {group.items.map((athlete, ai) => (
                                <button
                                    key={athlete.id}
                                    onClick={() => handleAthleteClick(athlete)}
                                    disabled={transferMode && athlete.kind !== "student"}
                                    data-testid={`athlete-active-item-${gi}-${ai}`}
                                    className={`flex items-center gap-3 p-4 text-left group transition-colors duration-300 ${transferMode && transferIds.includes(athlete.id) ? "bg-racing-red" : "bg-white hover:bg-racing-red"} ${transferMode && athlete.kind !== "student" ? "opacity-45 cursor-not-allowed" : ""}`}
                                >
                                    {transferMode && (
                                        <span
                                            className={`w-5 h-5 border flex items-center justify-center text-xs font-bold shrink-0 ${transferIds.includes(athlete.id) ? "border-white bg-white text-racing-red" : "border-racing-line text-transparent group-hover:border-white"}`}
                                            data-testid={`class-transfer-check-${gi}-${ai}`}
                                        >
                                            ✓
                                        </span>
                                    )}
                                    {athlete.photo_url ? (
                                        <img src={fileUrl(athlete.photo_url)} alt="" className="w-11 h-11 object-cover border border-racing-line shrink-0" />
                                    ) : (
                                        <span className="w-11 h-11 bg-racing-surface border border-racing-line flex items-center justify-center font-display text-racing-red shrink-0">{athlete.name.charAt(0)}</span>
                                    )}
                                    <span className="min-w-0">
                                        <span className="block font-medium text-sm text-neutral-900 group-hover:text-white transition-colors truncate">{athlete.name}</span>
                                        {athlete.nia && <span className="block text-[10px] text-racing-golddeep group-hover:text-racing-gold transition-colors">NIA {athlete.nia}</span>}
                                        {athlete.ku && <span className="block text-[10px] font-semibold uppercase tracking-[0.1em] text-racing-golddeep group-hover:text-racing-gold transition-colors" data-testid={`athlete-ku-${gi}-${ai}`}>{athlete.ku}</span>}
                                        {athlete.kind === "student" && !athlete.activated && (
                                            <span className="block text-[10px] uppercase tracking-[0.1em] text-racing-red group-hover:text-white transition-colors" data-testid={`athlete-not-activated-${gi}-${ai}`}>Belum Aktif</span>
                                        )}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setDossier(null); } }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="athlete-dossier-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-wide text-2xl">
                            {selected?.name}
                            {dossier?.student?.ku && (
                                <span className="ml-3 inline-block align-middle bg-racing-gold text-neutral-900 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1" data-testid="dossier-ku-badge">{dossier.student.ku}</span>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    {loadingDetail && <p className="text-racing-muted py-8">Memuat detail...</p>}

                    {dossier && (
                        <div data-testid="athlete-dossier-content">
                            <div className="flex items-center justify-between gap-3 mt-0 mb-3 border-b border-racing-line pb-2">
                                <h4 className="text-[10px] uppercase tracking-[0.25em] text-racing-golddeep">Data Diri</h4>
                                <div className="flex flex-wrap gap-2">
                                    {isAdmin && selected?.kind === "student" && !dossier.athlete && (
                                        <button type="button" onClick={openActivate} data-testid="dossier-activate-athlete-button" className="inline-flex items-center gap-2 bg-racing-red px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-white hover:bg-racing-redhover transition-colors">
                                            <UserCheck size={14} /> Aktifkan Atlet
                                        </button>
                                    )}
                                    <a
                                        href={`/kartu/admin/${selected?.id}?kind=${selected?.kind || "student"}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="dossier-athlete-card-button"
                                        className="inline-flex items-center gap-2 border border-racing-gold px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-golddeep hover:bg-racing-gold/10 transition-colors"
                                    >
                                        <IdentificationCard size={14} /> Kartu Atlet
                                    </a>
                                    {!isFinance && (
                                        <>
                                    <button type="button" onClick={selected?.kind === "public" ? openPublicEdit : openStudentEdit} data-testid="dossier-edit-athlete-button" className="inline-flex items-center gap-2 border border-racing-line px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors">
                                        <PencilSimple size={14} /> Edit Atlet
                                    </button>
                                    <button type="button" onClick={deleteSelectedAthlete} disabled={saving} data-testid="dossier-delete-athlete-button" className="inline-flex items-center gap-2 border border-racing-red px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-red hover:bg-racing-red hover:text-white disabled:opacity-50 transition-colors">
                                        <Trash size={14} /> Hapus Atlet
                                    </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-5 items-start">
                                {dossier.student.photo_url ? (
                                    <img src={fileUrl(dossier.student.photo_url)} alt="" className="w-24 h-32 object-cover border border-racing-line" />
                                ) : null}
                                <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 text-sm flex-1">
                                    <div><dt className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">NIA</dt><dd className="font-medium text-racing-golddeep">{dossier.athlete?.nia || "—"}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Kelas</dt><dd className="font-medium">{dossier.student.class_name || "—"}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Kelompok Umur</dt><dd className="font-medium" data-testid="dossier-ku-value">{dossier.student.ku || "—"}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Status</dt><dd className="font-medium">{dossier.student.status || "—"}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Panggilan</dt><dd>{dossier.student.nickname || "—"}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Lahir</dt><dd>{dossier.student.birth_place ? `${dossier.student.birth_place}, ` : ""}{dossier.student.birth_date || "—"}</dd></div>
                                    <div><dt className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Bergabung</dt><dd>{dossier.student.joined_date || "—"}</dd></div>
                                    <div className="col-span-2"><dt className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Alamat</dt><dd>{dossier.student.address || "—"}</dd></div>
                                </dl>
                            </div>

                            <SectionTitle>Akun Orang Tua</SectionTitle>
                            {dossier.parent ? (
                                <div data-testid="dossier-parent-account">
                                    <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
                                        <span><span className="text-racing-muted">Username:</span> <span className="font-medium">{dossier.parent.username || "—"}</span></span>
                                        <span><span className="text-racing-muted">Email:</span> {dossier.parent.email || "—"}</span>
                                        <span><span className="text-racing-muted">Nama:</span> {dossier.parent.name || "—"}</span>
                                        <span><span className="text-racing-muted">Status:</span> <span className="font-medium capitalize">{dossier.parent.status || "active"}</span></span>
                                        {(dossier.student.student_phone || dossier.parent.contact_phone) && (
                                            <a
                                                href={waLink(dossier.student.student_phone || dossier.parent.contact_phone, `Assalamu'alaikum, kami dari Mandalika Inlineskate.`)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid="dossier-parent-wa"
                                                className="inline-flex items-center gap-1.5 text-green-700 hover:text-green-800 font-medium"
                                            >
                                                <WhatsappLogo size={16} weight="fill" /> {dossier.student.student_phone || dossier.parent.contact_phone}
                                            </a>
                                        )}
                                    </div>
                                    {dossier.parent.status !== "disabled" && !isFinance ? (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <button type="button" onClick={openAccountEdit} data-testid="dossier-edit-parent-button" className="inline-flex items-center gap-2 border border-racing-line px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors">
                                                <PencilSimple size={14} /> Edit Akun Ortu
                                            </button>
                                            <button type="button" onClick={openPasswordEdit} data-testid="dossier-reset-parent-password-button" className="inline-flex items-center gap-2 border border-racing-line px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors">
                                                <Key size={14} /> Reset Password
                                            </button>
                                        </div>
                                    ) : dossier.parent.status === "disabled" ? (
                                        <div className="mt-3" data-testid="dossier-parent-disabled">
                                            <p className="text-xs text-racing-muted mb-2">Akun ini nonaktif. Aktifkan kembali agar orang tua bisa login — username/email asli dipulihkan otomatis.</p>
                                            {isAdmin && (
                                                <button type="button" onClick={reactivateParent} disabled={saving} data-testid="dossier-reactivate-parent-button" className="inline-flex items-center gap-2 bg-racing-red px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-white hover:bg-racing-redhover disabled:opacity-50 transition-colors">
                                                    <UserCheck size={14} /> Aktifkan Akun Ortu
                                                </button>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <p className="text-sm text-racing-muted">Tidak ada akun orang tua terhubung.</p>
                            )}

                            <SectionTitle>Prestasi ({dossier.achievements.length})</SectionTitle>
                            {dossier.achievements.length === 0 ? <p className="text-sm text-racing-muted">Belum ada prestasi.</p> : (
                                <div className="border border-racing-line">
                                    {dossier.achievements.map((a, i) => (
                                        <div key={a.id || i} className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-3 border-b border-racing-line last:border-0 text-sm" data-testid={`dossier-achievement-${i}`}>
                                            <span className="font-medium uppercase">{a.medal}</span>
                                            <span className="font-medium">{a.title}</span>
                                            <span className="text-racing-muted">{a.event}{a.level ? ` • ${a.level}` : ""} • {a.year}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {supportData && (
                                <div data-testid="dossier-support-section">
                                    <SectionTitle>Dukungan Publik ({supportData.like_count} love, {supportData.comments.length} komentar)</SectionTitle>
                                    {supportData.comments.length === 0 ? <p className="text-sm text-racing-muted">Belum ada komentar dukungan.</p> : (
                                        <div className="border border-racing-line">
                                            {supportData.comments.map((c, i) => (
                                                <div key={c.id} className="flex items-start justify-between gap-4 px-4 py-3 border-b border-racing-line last:border-0 text-sm" data-testid={`dossier-support-${i}`}>
                                                    <div>
                                                        <p className="text-[10px] uppercase tracking-[0.15em] text-racing-golddeep">{c.name}</p>
                                                        <p className="mt-0.5">{c.message}</p>
                                                    </div>
                                                    <button type="button" onClick={() => deleteSupport(c.id)} data-testid={`support-delete-${i}`} className="shrink-0 text-racing-muted hover:text-racing-red transition-colors" title="Hapus komentar">
                                                        <Trash size={15} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <SectionTitle>Catatan Waktu ({dossier.time_records.length})</SectionTitle>
                            {dossier.time_records.length === 0 ? <p className="text-sm text-racing-muted">Belum ada catatan waktu.</p> : (
                                <div className="space-y-6" data-testid="dossier-time-groups">
                                    {timeByRace.map((group, gi) => (
                                        <div key={group.name} className="border border-racing-line" data-testid={`dossier-time-group-${gi}`}>
                                            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3 border-b border-racing-line bg-racing-surface">
                                                <span className="font-display uppercase text-lg text-neutral-900">{group.name}</span>
                                                <span className="text-xs text-racing-muted">{group.items.length} catatan</span>
                                                {group.best && (
                                                    <span className="ml-auto px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] border border-racing-gold text-racing-golddeep bg-racing-gold/10" data-testid={`dossier-time-group-best-${gi}`}>
                                                        Terbaik {group.best.time_text}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="px-2 pt-3 border-b border-racing-line" data-testid={`dossier-time-chart-${gi}`}>
                                                <TimeProgressChart records={group.items} />
                                                <p className="px-2 pb-2 text-[10px] text-racing-muted">Garis naik = waktu semakin kecil, berarti semakin kencang.</p>
                                            </div>
                                            <div>
                                                {group.items.map((r, i) => (
                                                    <div key={r.id || i} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3 border-b border-racing-line last:border-0 text-sm" data-testid={`dossier-time-${gi}-${i}`}>
                                                        <span className="font-display text-lg text-racing-red">{r.time_text}</span>
                                                        <span>{r.event_number}</span>
                                                        <span className="text-racing-muted">{r.record_date}</span>
                                                        {r.note && <span className="text-racing-muted italic" data-testid={`dossier-time-note-${gi}-${i}`}>&ldquo;{r.note}&rdquo;</span>}
                                                        {r.is_best && <span className="px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] border border-racing-gold text-racing-golddeep bg-racing-gold/10">Terbaik</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <SectionTitle>Rapot ({dossier.report_cards.length})</SectionTitle>
                            {dossier.report_cards.length === 0 ? <p className="text-sm text-racing-muted">Belum ada rapot.</p> : (
                                <div className="border border-racing-line">
                                    {dossier.report_cards.map((r, i) => (
                                        <div key={r.id || i} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 border-b border-racing-line last:border-0 text-sm" data-testid={`dossier-report-${i}`}>
                                            <span className="font-medium">{r.template_title || "Rapot"}</span>
                                            <span className="text-racing-muted">{r.period} • oleh {r.coach_name || "—"}</span>
                                            <a href={`${api.defaults.baseURL}/report-cards/${r.id}/export?format=pdf`} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs uppercase tracking-[0.15em] text-racing-red" data-testid={`dossier-report-pdf-${i}`}>PDF</a>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <SectionTitle>Absensi ({dossier.attendance.length} catatan)</SectionTitle>
                            {dossier.attendance.length === 0 ? <p className="text-sm text-racing-muted">Belum ada catatan absensi.</p> : (
                                <>
                                    <div className="flex gap-3 mb-3 text-xs">
                                        {["hadir", "izin", "sakit", "alpha"].map((s) => (
                                            <span key={s} className="px-3 py-1.5 border border-racing-line uppercase tracking-[0.1em]" data-testid={`dossier-attendance-count-${s}`}>{s}: {attendanceStats[s] || 0}</span>
                                        ))}
                                    </div>
                                    <div className="border border-racing-line max-h-56 overflow-y-auto">
                                        {dossier.attendance.slice(0, 40).map((r, i) => (
                                            <div key={i} className="flex gap-4 px-4 py-2.5 border-b border-racing-line last:border-0 text-sm">
                                                <span className="text-racing-muted w-24">{r.date}</span>
                                                <span className={`uppercase font-medium ${r.status === "hadir" ? "text-green-700" : "text-racing-red"}`}>{r.status}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            <SectionTitle>Iuran ({dossier.payments.length} pembayaran)</SectionTitle>
                            {dossier.payments.length === 0 ? <p className="text-sm text-racing-muted">Belum ada pembayaran iuran.</p> : (
                                <div className="border border-racing-line">
                                    {dossier.payments.map((p, i) => (
                                        <div key={p.id || i} className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-3 border-b border-racing-line last:border-0 text-sm" data-testid={`dossier-payment-${i}`}>
                                            <span className="font-medium">{p.period_month}</span>
                                            <span>Rp {Number(p.amount || 0).toLocaleString("id-ID")}</span>
                                            <span className="text-racing-muted">{p.payment_date} • {p.method} • {p.receipt_no}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog === "student"} onOpenChange={(open) => { if (!open) setEditDialog(""); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="admin-edit-student-dialog">
                    <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Edit Data Atlet</DialogTitle></DialogHeader>
                    {studentForm && (
                        <form onSubmit={saveStudent} className="grid sm:grid-cols-2 gap-4 mt-2" data-testid="admin-edit-student-form">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Lengkap</label>
                                <input required value={studentForm.name} onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })} data-testid="admin-student-name-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Panggilan</label>
                                <input value={studentForm.nickname} onChange={(e) => setStudentForm({ ...studentForm, nickname: e.target.value })} data-testid="admin-student-nickname-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">NIA</label>
                                <input value={studentForm.nia} onChange={(e) => setStudentForm({ ...studentForm, nia: e.target.value })} data-testid="admin-student-nia-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" placeholder="Contoh: 167" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas</label>
                                <select required value={studentForm.class_name} onChange={(e) => setStudentForm({ ...studentForm, class_name: e.target.value })} data-testid="admin-student-class-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm">
                                    <option value="" disabled>Pilih kelas</option>
                                    {CLASS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Status</label>
                                <select value={studentForm.status} onChange={(e) => setStudentForm({ ...studentForm, status: e.target.value })} data-testid="admin-student-status-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm">
                                    <option value="Aktif">Aktif</option>
                                    <option value="Menunggu ACC">Menunggu ACC</option>
                                    <option value="Ditolak">Ditolak</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Jenis Kelamin</label>
                                <select value={studentForm.gender} onChange={(e) => setStudentForm({ ...studentForm, gender: e.target.value })} data-testid="admin-student-gender-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm">
                                    <option value="">Pilih</option>
                                    <option value="Laki-laki">Laki-laki</option>
                                    <option value="Perempuan">Perempuan</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tanggal Lahir</label>
                                <input required type="date" value={studentForm.birth_date} onChange={(e) => setStudentForm({ ...studentForm, birth_date: e.target.value })} data-testid="admin-student-birth-date-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tempat Lahir</label>
                                <input value={studentForm.birth_place} onChange={(e) => setStudentForm({ ...studentForm, birth_place: e.target.value })} data-testid="admin-student-birth-place-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Masuk Mandalis Sejak</label>
                                <input required type="number" min="2000" max={new Date().getFullYear() + 1} value={studentForm.joined_year} onChange={(e) => setStudentForm({ ...studentForm, joined_year: e.target.value })} data-testid="admin-student-joined-year-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tanggal Masuk (1-31)</label>
                                <input type="number" min="1" max="31" value={studentForm.joined_day} onChange={(e) => setStudentForm({ ...studentForm, joined_day: e.target.value })} data-testid="admin-student-joined-day-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" placeholder="-" />
                                <p className="mt-1 text-[10px] text-racing-muted">Pengingat iuran email ke orang tua tiap tanggal ini setiap bulan. Kosongkan untuk nonaktif.</p>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Bulan Masuk Klub</label>
                                <input type="month" value={studentForm.joined_month} onChange={(e) => setStudentForm({ ...studentForm, joined_month: e.target.value })} data-testid="admin-student-joined-month-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                                <p className="mt-1 text-[10px] text-racing-muted">Ceklist iuran bulan sebelum ini ditandai "belum terdaftar". Kosongkan = mulai September 2026.</p>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Agama</label>
                                <input value={studentForm.religion} onChange={(e) => setStudentForm({ ...studentForm, religion: e.target.value })} data-testid="admin-student-religion-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kode Promo</label>
                                <input value={studentForm.promo_code} onChange={(e) => setStudentForm({ ...studentForm, promo_code: e.target.value })} data-testid="admin-student-promo-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Alamat Domisili</label>
                                <textarea rows={3} value={studentForm.address} onChange={(e) => setStudentForm({ ...studentForm, address: e.target.value })} data-testid="admin-student-address-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Alamat Sesuai KK</label>
                                <textarea rows={3} value={studentForm.address_kk} onChange={(e) => setStudentForm({ ...studentForm, address_kk: e.target.value })} data-testid="admin-student-address-kk-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <p className="sm:col-span-2 text-xs text-racing-muted">Foto atlet tetap tersimpan. Untuk crop/ganti foto, gunakan tab Akun Pendaftar.</p>
                            <button disabled={saving} data-testid="admin-save-student-button" className="sm:col-span-2 bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan Data Atlet"}</button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog === "athlete"} onOpenChange={(open) => { if (!open) setEditDialog(""); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-xl" data-testid="admin-edit-public-athlete-dialog">
                    <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Edit Atlet Publik</DialogTitle></DialogHeader>
                    {publicForm && (
                        <form onSubmit={savePublicAthlete} className="grid sm:grid-cols-2 gap-4 mt-2" data-testid="admin-edit-public-athlete-form">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">NIA</label>
                                <input value={publicForm.nia} onChange={(e) => setPublicForm({ ...publicForm, nia: e.target.value })} data-testid="admin-public-athlete-nia-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Lengkap</label>
                                <input required value={publicForm.name} onChange={(e) => setPublicForm({ ...publicForm, name: e.target.value })} data-testid="admin-public-athlete-name-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas</label>
                                <select value={publicForm.class_name} onChange={(e) => setPublicForm({ ...publicForm, class_name: e.target.value })} data-testid="admin-public-athlete-class-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm">
                                    <option value="">— Belum Ada Kelas —</option>
                                    {CLASS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Status</label>
                                <select value={publicForm.status} onChange={(e) => setPublicForm({ ...publicForm, status: e.target.value })} data-testid="admin-public-athlete-status-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm">
                                    <option value="active">Aktif</option>
                                    <option value="inactive">Nonaktif</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tahun Lahir</label>
                                <input required type="number" value={publicForm.birth_year} onChange={(e) => setPublicForm({ ...publicForm, birth_year: e.target.value })} data-testid="admin-public-athlete-birth-year-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Bergabung Sejak</label>
                                <input required type="number" value={publicForm.joined_year} onChange={(e) => setPublicForm({ ...publicForm, joined_year: e.target.value })} data-testid="admin-public-athlete-joined-year-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                            </div>
                            <p className="sm:col-span-2 text-xs text-racing-muted">Foto atlet tetap tersimpan. Untuk crop/ganti foto, gunakan tab Atlet.</p>
                            <button disabled={saving} data-testid="admin-save-public-athlete-button" className="sm:col-span-2 bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan Atlet"}</button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog === "account"} onOpenChange={(open) => { if (!open) setEditDialog(""); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-lg" data-testid="dossier-edit-account-dialog">
                    <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Edit Akun Orang Tua</DialogTitle></DialogHeader>
                    {accountForm && (
                        <form onSubmit={saveAccount} className="space-y-4 mt-2" data-testid="dossier-edit-account-form">
                            {[
                                ["username", "Username"],
                                ["email", "Email"],
                                ["whatsapp", "No. WhatsApp"],
                                ["father_name", "Nama Ayah"],
                                ["mother_name", "Nama Ibu"],
                            ].map(([name, label]) => (
                                <div key={name}>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">{label}</label>
                                    <input required={name !== "father_name" && name !== "mother_name"} value={accountForm[name]} onChange={(e) => setAccountForm({ ...accountForm, [name]: e.target.value })} data-testid={`dossier-account-${name}-input`} className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                                </div>
                            ))}
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Status Akun</label>
                                <select value={accountForm.status} onChange={(e) => setAccountForm({ ...accountForm, status: e.target.value })} data-testid="dossier-account-status-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                                    {accountForm.status === "pending" && <option value="pending">Menunggu ACC (aktifkan lewat tombol ACC)</option>}
                                    <option value="active">Aktif</option>
                                    <option value="rejected">Ditolak / Tidak Bisa Login</option>
                                </select>
                            </div>
                            <button disabled={saving} data-testid="dossier-save-account-button" className="w-full bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan Akun"}</button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog === "password"} onOpenChange={(open) => { if (!open) setEditDialog(""); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-md" data-testid="dossier-password-dialog">
                    <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Reset Password Orang Tua</DialogTitle></DialogHeader>
                    {passwordResult ? (
                        <div className="border border-racing-gold bg-racing-gold/10 p-5 mt-2" data-testid="dossier-password-result">
                            <p className="text-sm">Password baru untuk <strong>{passwordResult.username}</strong>:</p>
                            <p className="mt-2 font-display text-2xl text-racing-red break-all">{passwordResult.new_password}</p>
                            <button type="button" onClick={() => setEditDialog("")} data-testid="dossier-password-done-button" className="mt-5 w-full bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em]">Selesai</button>
                        </div>
                    ) : (
                        <form onSubmit={savePassword} className="space-y-4 mt-2" data-testid="dossier-password-form">
                            <p className="text-sm text-racing-muted">Kosongkan jika ingin sistem membuat password otomatis.</p>
                            <input value={passwordForm.new_password} onChange={(e) => setPasswordForm({ new_password: e.target.value })} data-testid="dossier-new-password-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" placeholder="Password baru" />
                            <button disabled={saving} data-testid="dossier-save-password-button" className="w-full bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan Password"}</button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={!!activateForm} onOpenChange={(open) => { if (!open) { setActivateForm(null); setActivateResult(null); } }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-md" data-testid="activate-dialog">
                    <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Aktifkan Atlet</DialogTitle></DialogHeader>
                    {activateResult ? (
                        <div data-testid="activate-success">
                            <div className="mt-2 space-y-2 text-sm border border-racing-line bg-racing-surface p-4">
                                <p><span className="text-racing-muted">Atlet:</span> <span className="font-medium">{selected?.name}</span></p>
                                <p><span className="text-racing-muted">NIA:</span> <span className="font-display text-racing-red">{activateResult.nia}</span></p>
                                <p><span className="text-racing-muted">Username:</span> <span className="font-medium break-all">{activateResult.username}</span></p>
                                {activateForm?.password && <p><span className="text-racing-muted">Password:</span> <span className="font-medium break-all">{activateForm.password}</span></p>}
                            </div>
                            <p className="mt-3 text-xs text-racing-muted">Atlet kini tampil di daftar publik, absensi, dan iuran.</p>
                            {activateResult.parent_phone && (
                                <a
                                    href={`https://wa.me/${activateResult.parent_phone}?text=${encodeURIComponent(`Halo, akun Portal Orang Tua Mandalika Inlineskate untuk ananda ${selected?.name} sudah aktif.\n\nUsername: ${activateResult.username}\nPassword: ${activateForm?.password || "(password lama tetap berlaku)"}\n\nLogin di website klub, menu Portal Orang Tua.`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="activate-whatsapp-link"
                                    className="mt-4 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-xs uppercase tracking-[0.2em] py-3 transition-colors"
                                >
                                    <WhatsappLogo size={16} weight="bold" /> Kirim Info Login via WhatsApp
                                </a>
                            )}
                            <button type="button" onClick={() => { setActivateForm(null); setActivateResult(null); }} data-testid="activate-done" className="mt-3 w-full border border-racing-line text-racing-muted text-xs uppercase tracking-[0.2em] py-3">Selesai</button>
                        </div>
                    ) : (
                        <div className="mt-2 space-y-4" data-testid="activate-form">
                            <p className="text-sm text-racing-muted">{selected?.name} akan tampil sebagai atlet publik (NIA otomatis) dan masuk daftar absensi &amp; iuran.</p>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Foto Atlet (opsional)</label>
                                {activateForm?.photo_url ? (
                                    <div className="flex items-center gap-3">
                                        <img src={fileUrl(activateForm.photo_url)} alt="Foto atlet" className="w-16 h-16 object-cover border border-racing-line" data-testid="activate-photo-preview" />
                                        <button type="button" onClick={() => setActivateForm({ ...activateForm, photo_url: "" })} className="text-[10px] uppercase tracking-[0.2em] text-racing-red">Hapus</button>
                                    </div>
                                ) : (
                                    <label className="flex items-center justify-center gap-2 border-2 border-dashed border-racing-line hover:border-racing-red p-4 cursor-pointer text-[10px] uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors" data-testid="activate-photo-dropzone">
                                        {activatePhotoUploading ? "Mengunggah..." : <><UploadSimple size={16} /> Pilih Foto</>}
                                        <input type="file" accept="image/*" className="hidden" data-testid="activate-photo-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadActivatePhoto(f); }} />
                                    </label>
                                )}
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Username Akun Orang Tua</label>
                                <input value={activateForm?.username || ""} onChange={(e) => setActivateForm({ ...activateForm, username: e.target.value })} data-testid="activate-username-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" />
                                <p className="mt-1.5 text-xs text-racing-muted">Tanpa spasi, minimal 3 karakter.</p>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Password {dossier?.parent ? "Baru (kosongkan jika tidak diganti)" : "Akun (wajib, min. 6 karakter)"}</label>
                                <input value={activateForm?.password || ""} onChange={(e) => setActivateForm({ ...activateForm, password: e.target.value })} data-testid="activate-password-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red" placeholder={dossier?.parent ? "••••••" : "buat password awal"} />
                            </div>
                            <div className="flex gap-3">
                                <button onClick={submitActivate} disabled={activating || activatePhotoUploading} data-testid="activate-submit" className="flex-1 bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] py-3.5 transition-colors">
                                    {activating ? "Mengaktifkan..." : "Aktifkan Sekarang"}
                                </button>
                                <button type="button" onClick={() => setActivateForm(null)} data-testid="activate-cancel" className="flex-1 border border-racing-line text-racing-muted text-xs uppercase tracking-[0.2em] py-3.5">Batal</button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
